import { useEffect, useState } from 'react'
import {
  getAssessmentHistoryForLearner,
  getReport,
  type AssessmentReport,
} from '../../../assessment'
import type { LearnerAccount } from '../../../accounts'
import { parseGroupNum } from '../shared/groupUtils'

interface UseLatestAssessmentResult {
  report: AssessmentReport | null
  isLoading: boolean
}

/**
 * Fetch the most recent assessment report for a learner+group.
 *
 * Replaces the duplicated effect pattern previously inlined on HomePage and
 * VoortgangPage (~30 lines each, identical logic). Handles:
 *  - async cancellation when the dependency changes mid-flight (no setState
 *    on a stale closure when the parent re-renders for another child)
 *  - graceful no-child state (returns `null` immediately)
 *  - swallows fetch errors silently — the parent shows a "no assessment yet"
 *    placeholder card in both error and empty cases (matches current UX)
 *
 * Audit ref: PARENT_PORTAL_UX_AUDIT_2026 §18.4 F-RUN-NEW-5.
 */
export function useLatestAssessment(
  selectedChild: LearnerAccount | undefined,
  selectedGroup: string,
): UseLatestAssessmentResult {
  const [report, setReport] = useState<AssessmentReport | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(selectedChild))

  useEffect(() => {
    let cancelled = false

    if (!selectedChild) {
      setReport(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    const groupNum = parseGroupNum(selectedGroup)

    // AS-2026-07-P1-01: scoping is enforced by the accessor, not by a filter
    // this call-site has to remember to apply.
    getAssessmentHistoryForLearner(selectedChild.id, groupNum)
      .then((childHistory) => {
        if (cancelled) return undefined
        if (childHistory.length === 0) {
          setReport(null)
          setIsLoading(false)
          return undefined
        }
        // Already sorted newest-first by the accessor; copy before sorting so
        // we never mutate a caller-visible array.
        const newestFirst = [...childHistory].sort(
          (a, b) => b.completedAt - a.completedAt,
        )
        return getReport(newestFirst[0].attemptId)
      })
      .then((fetched) => {
        if (cancelled) return
        if (fetched !== undefined) {
          setReport(fetched || null)
          setIsLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReport(null)
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [selectedChild?.id, selectedGroup])

  return { report, isLoading }
}
